import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createAirlineClass,
  getAirlineClassByID,
  getAllAirlineClasses,
  updateAirlineClass,
} from "./services/airlineclass.services";
import { clearAirlineClassDetails } from "./airlineclassSlice";
import { closed } from "../../components/drawer/drawerSlice";

const CreateAirlineClass: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const AirlineClassDetail = useAppSelector(
    (state) => state.airlineclass.AirlineClassDetail
  );
  const AirlineClassID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.airlineclass.formLoading);

  useEffect(() => {
    return () => {
      dispatch(clearAirlineClassDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (AirlineClassID) {
      dispatch(getAirlineClassByID(AirlineClassID));
    }
  }, [AirlineClassID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!AirlineClassID) {
        const insertPayload = {
          ClassName: values.ClassName,
          IsActive: values.IsActive ? values.IsActive : true,
        };
        const response = await dispatch(createAirlineClass(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearAirlineClassDetails());
          dispatch(getAllAirlineClasses());
        }
      } else {
        const editPayload = {
          ID: AirlineClassID,
          ClassName: values.ClassName,
          IsActive: values.IsActive ? values.IsActive : false,
        };
        const response = await dispatch(updateAirlineClass(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearAirlineClassDetails());
          dispatch(getAllAirlineClasses());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={AirlineClassDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="ClassName"
                label="Airline Class"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginRight: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
              <ButtonWithLoading
                label="Save"
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateAirlineClass;
